import React, { useState, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ArrowDownLeft,
  ArrowUpRight,
  ArrowLeft,
  Pencil,
  Trash2,
  Search,
  Filter,
  X,
  Building2,
  Calendar,
  AlertTriangle,
  Eye,
  EyeOff,
} from "lucide-react";
import { useLocation } from "wouter";
import { CATEGORY_LABELS, TRANSACTION_CATEGORIES, formatAmount } from "@shared/types";
import type { TransactionCategory } from "@shared/types";
import { useLanguage, CATEGORY_LABELS_AR } from "@/contexts/LanguageContext";
import { RouteFeedback } from "@/components/RouteFeedback";

/** Format account label for display in dropdown */
function formatAccountLabel(accountNumber: string, bankSource?: string | null, accountLabel?: string | null): string {
  if (accountLabel) return accountLabel;
  if (accountNumber.endsWith('-Main')) {
    const bank = accountNumber.replace('-Main', '');
    return `${bank} Account`;
  }
  if (accountNumber.startsWith('xx')) {
    const digits = accountNumber.replace('xx', '');
    const bank = bankSource?.includes('KFH') ? 'KFH' : (bankSource || '');
    return `${bank} ••${digits}`;
  }
  if (accountNumber === 'KFH-other') return 'KFH Other';
  if (/^\d+$/.test(accountNumber)) {
    const bank = bankSource || '';
    const shortBank = bank.includes('NBK') || bank.includes('National Bank') ? 'NBK'
      : bank.includes('Boubyan') ? 'Boubyan'
      : bank.includes('KFH') ? 'KFH'
      : bank;
    return `${shortBank} ${accountNumber}`;
  }
  return accountNumber;
}

export default function Transactions() {
  const { t, language, isRTL } = useLanguage();
  const getCategoryLabel = (cat: string) => { const c = cat === "bills_utilities" ? "bills" : cat; return language === "ar" ? (CATEGORY_LABELS_AR[c] ?? CATEGORY_LABELS[c as TransactionCategory] ?? c.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())) : (CATEGORY_LABELS[c as TransactionCategory] ?? c.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())); };
  const [monthFilter, setMonthFilter] = useState<string>(() => {
    const params = new URLSearchParams(window.location.search);
    const urlMonth = params.get("month");
    if (urlMonth) return urlMonth;
    return "all";
  });
  const [typeFilter, setTypeFilter] = useState<string>(() => {
    const params = new URLSearchParams(window.location.search);
    const urlType = params.get("type");
    if (urlType === "cash_in" || urlType === "cash_out") return urlType;
    return "all";
  });
  const [categoryFilter, setCategoryFilter] = useState<string>(() => {
    const params = new URLSearchParams(window.location.search);
    const urlCategory = params.get("category");
    if (urlCategory) return urlCategory;
    return "all";
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [accountFilter, setAccountFilter] = useState<string>(() => {
    const params = new URLSearchParams(window.location.search);
    const urlAccount = params.get("account");
    if (urlAccount) return urlAccount;
    return "all";
  });
  const [showAdjustments, setShowAdjustments] = useState(true);
  const [editingTxn, setEditingTxn] = useState<any>(null);
  const [editForm, setEditForm] = useState({ merchant: "", category: "", type: "", amount: "", notes: "" });

  // Fetch available months and accounts
  const availableMonthsQuery = trpc.transaction.availableMonths.useQuery();
  const accountsQuery = trpc.transaction.accounts.useQuery();
  const { data: availableMonths } = availableMonthsQuery;
  const { data: accounts } = accountsQuery;

  // Build month options from available data
  const monthOptions = useMemo(() => {
    if (!availableMonths || availableMonths.length === 0) return [];
    return availableMonths.map((m) => {
      const d = new Date(m.year, m.month - 1, 1);
      const label = d.toLocaleString(language === "ar" ? "ar-KW" : "default", { month: "long", year: "numeric" });
      return { value: `${m.year}-${m.month - 1}`, label };
    });
  }, [availableMonths, language]);

  // Derive startDate/endDate from selected month
  const { startDate, endDate } = useMemo(() => {
    if (monthFilter === "all") return { startDate: undefined, endDate: undefined };
    const [yearStr, monthStr] = monthFilter.split("-");
    const year = parseInt(yearStr);
    const month = parseInt(monthStr);
    const start = new Date(year, month, 1).getTime();
    const end = new Date(year, month + 1, 0, 23, 59, 59, 999).getTime();
    return { startDate: start, endDate: end };
  }, [monthFilter]);

  const queryInput = useMemo(() => ({
    type: typeFilter !== "all" ? (typeFilter as "cash_in" | "cash_out") : undefined,
    category: categoryFilter !== "all" ? categoryFilter : undefined,
    startDate,
    endDate,
    accountNumber: accountFilter !== "all" ? accountFilter : undefined,
    includeAdjustments: showAdjustments ? undefined : false,
    limit: 200,
    offset: 0,
  }), [typeFilter, categoryFilter, startDate, endDate, accountFilter, showAdjustments]);

  // Separate query for fetching all categories (only type filter applied, no category filter)
  const allCatsQueryInput = useMemo(() => ({
    type: typeFilter !== "all" ? (typeFilter as "cash_in" | "cash_out") : undefined,
    limit: 200,
    offset: 0,
  }), [typeFilter]);

  const transactionsQuery = trpc.transaction.list.useQuery(queryInput);
  const categoryTransactionsQuery = trpc.transaction.list.useQuery(allCatsQueryInput);
  const { data: txns } = transactionsQuery;
  const { data: allTxnsForCats } = categoryTransactionsQuery;
  const pageQueries = [availableMonthsQuery, accountsQuery, transactionsQuery, categoryTransactionsQuery];
  const isPageLoading = pageQueries.some((query) => query.isLoading);
  const isPageError = pageQueries.some((query) => query.isError);
  const retryPageQueries = () => { pageQueries.forEach((query) => { void query.refetch(); }); };
  const utils = trpc.useUtils();

  const updateMutation = trpc.transaction.update.useMutation({
    onSuccess: () => {
      toast.success("Transaction updated");
      setEditingTxn(null);
      utils.transaction.list.invalidate();
      utils.transaction.summary.invalidate();
      utils.transaction.categoryBreakdown.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const deleteMutation = trpc.transaction.delete.useMutation({
    onSuccess: () => {
      toast.success("Transaction deleted");
      utils.transaction.list.invalidate();
      utils.transaction.summary.invalidate();
      utils.transaction.categoryBreakdown.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const filtered = useMemo(() => {
    if (!txns) return [];
    if (!searchQuery.trim()) return txns;
    const q = searchQuery.toLowerCase();
    return txns.filter(
      (t) =>
        (t.merchant ?? "").toLowerCase().includes(q) ||
        (t.category ?? "").toLowerCase().includes(q) ||
        (t.sourceMessage ?? "").toLowerCase().includes(q) ||
        (t.bankSource ?? "").toLowerCase().includes(q) ||
        String(t.amount).includes(q)
    );
  }, [txns, searchQuery]);

  // Dynamic categories: only show categories that exist in user's actual transactions
  // Uses the unfiltered-by-category query so all categories remain visible in dropdown
  const availableCategories = useMemo(() => {
    if (!allTxnsForCats) return [];
    const cats = new Set<string>();
    allTxnsForCats.forEach((t) => { if (t.category) cats.add(t.category); });
    return Array.from(cats).sort();
  }, [allTxnsForCats]);

  const openEdit = (txn: any) => {
    setEditingTxn(txn);
    setEditForm({
      merchant: txn.merchant ?? "",
      category: txn.category ?? "uncategorized",
      type: txn.type,
      amount: txn.amount,
      notes: txn.notes ?? "",
    });
  };

  const handleSaveEdit = () => {
    if (!editingTxn) return;
    updateMutation.mutate({
      id: editingTxn.id,
      merchant: editForm.merchant,
      category: editForm.category,
      type: editForm.type as "cash_in" | "cash_out",
      amount: editForm.amount,
      notes: editForm.notes || undefined,
    });
  };

  const hasFilters = typeFilter !== "all" || categoryFilter !== "all" || accountFilter !== "all" || monthFilter !== "all" || searchQuery.trim() !== "";
  const [, setLocation] = useLocation();
  // Detect if user navigated here with pre-applied filters (from dashboard or other pages)
  const hasUrlFilters = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.has("type") || params.has("category");
  }, []);

  if (isPageLoading || isPageError) {
    return <div dir={isRTL ? "rtl" : "ltr"}><RouteFeedback isLoading={isPageLoading} isError={isPageError} onRetry={retryPageQueries} label={t("transactions.title")} /></div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6" dir={isRTL ? "rtl" : "ltr"}>
      <div className="flex items-center gap-3">
        {hasUrlFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.history.back()}
            className="gap-1.5 h-8 px-2 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-xs sm:text-sm">{language === "ar" ? "رجوع" : "Back"}</span>
          </Button>
        )}
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-foreground">{t("transactions.title")}</h1>
          <p className="text-muted-foreground text-xs sm:text-sm mt-1">
            {t("transactions.subtitle")}
          </p>
        </div>
      </div>

      {/* Filters -- gradient background like Budgets summary */}
      <div className="rounded-xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/20 p-3 sm:p-5">
          <div className="flex flex-col gap-2 sm:gap-3">
            <div className="relative">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t("transactions.search")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="min-h-11 ps-9 text-sm"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="min-h-11 w-[120px] text-sm sm:w-[160px]">
                  <Filter className="me-1 h-4 w-4 shrink-0 hidden sm:me-2 sm:block" />
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("transactions.allTypes")}</SelectItem>
                  <SelectItem value="cash_in">{t("field.cashIn")}</SelectItem>
                  <SelectItem value="cash_out">{t("field.cashOut")}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="min-h-11 min-w-[120px] flex-1 text-sm sm:w-[180px]">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("transactions.allCategories")}</SelectItem>
                  {availableCategories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {getCategoryLabel(cat)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={monthFilter} onValueChange={setMonthFilter}>
                <SelectTrigger className="min-h-11 w-[130px] text-sm sm:w-[170px]">
                  <Calendar className="me-1 h-4 w-4 shrink-0 hidden sm:me-2 sm:block" />
                  <SelectValue placeholder={language === "ar" ? "كل الأشهر" : "All Months"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === "ar" ? "كل الأشهر" : "All Months"}</SelectItem>
                  {monthOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {accounts && accounts.filter((acc) => acc.accountNumber != null).length > 0 && (
                <Select value={accountFilter} onValueChange={setAccountFilter}>
                  <SelectTrigger className="min-h-11 w-[130px] text-sm sm:w-[170px]">
                    <Building2 className="me-1 h-4 w-4 shrink-0 hidden sm:me-2 sm:block" />
                    <SelectValue placeholder={language === "ar" ? "كل الحسابات" : "All Accounts"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{language === "ar" ? "كل الحسابات" : "All Accounts"}</SelectItem>
                    {accounts.filter((acc) => acc.accountNumber != null && acc.isActive !== 0).map((acc) => (
                      <SelectItem key={acc.accountNumber!} value={acc.accountNumber!}>
                        {formatAccountLabel(acc.accountNumber!, acc.bankSource, acc.accountLabel)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {searchQuery.trim() && (
                <Button
                  variant="default"
                  size="icon"
                  className="h-9 w-9 sm:h-10 sm:w-10 flex-shrink-0"
                  onClick={() => {
                    // Search is already live, this is a visual indicator
                  }}
                >
                  <Search className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant={showAdjustments ? "outline" : "default"}
                size="sm"
                className="h-9 sm:h-10 text-xs sm:text-sm gap-1 px-2 sm:px-3 flex-shrink-0"
                onClick={() => setShowAdjustments(!showAdjustments)}
                title={showAdjustments ? (language === "ar" ? "إخفاء التسويات" : "Hide adjustments") : (language === "ar" ? "إظهار التسويات" : "Show adjustments")}
              >
                {showAdjustments ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                <span className="hidden sm:inline">{showAdjustments ? (language === "ar" ? "التسويات" : "Adj.") : (language === "ar" ? "التسويات" : "Adj.")}</span>
              </Button>
              {hasFilters && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 sm:h-10 sm:w-10 flex-shrink-0"
                  onClick={() => {
                    setTypeFilter("all");
                    setCategoryFilter("all");
                    setAccountFilter("all");
                    setMonthFilter("all");
                    setSearchQuery("");
                    setShowAdjustments(true);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
      </div>

      {/* Transaction List -- accent border like Budgets tracking */}
      <Card className="border-l-4 border-l-primary">
        <CardHeader className="pb-2 sm:pb-3 px-3 sm:px-6 pt-3 sm:pt-6">
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 rounded-md bg-primary/10 flex items-center justify-center">
              <Filter className="h-3.5 w-3.5 text-primary" />
            </div>
            <CardTitle className="text-sm sm:text-base">
              {filtered.length} Transaction{filtered.length !== 1 ? "s" : ""}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
          {filtered.length === 0 ? (
            <div className="text-center py-8 sm:py-12 text-muted-foreground">
              {hasFilters ? (
                <>
                  <Search className="h-7 w-7 sm:h-8 sm:w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-xs sm:text-sm">No transactions match your filters</p>
                  <p className="text-[10px] sm:text-xs mt-1">Try adjusting your filters or clearing them</p>
                </>
              ) : (
                <>
                  <ArrowDownLeft className="h-7 w-7 sm:h-8 sm:w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-xs sm:text-sm font-medium">No transactions yet</p>
                  <p className="text-[10px] sm:text-xs mt-1 max-w-xs mx-auto">
                    Go to the Upload Transactions page and upload your bank statements or paste SMS messages to start tracking.
                  </p>
                  <Button variant="outline" size="sm" className="mt-4 text-xs" onClick={() => window.location.href = '/sms'}>
                    Upload Transactions
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-1.5 sm:space-y-2">
              {filtered.map((txn) => (
                <div
                  key={txn.id}
                  className={`p-2.5 sm:p-4 rounded-lg border transition-colors border-l-2 ${txn.isAdjustment ? "bg-amber-50/50 dark:bg-amber-950/20 border-dashed border-amber-300 dark:border-amber-700" : "bg-card hover:bg-accent/50"}`}
                  style={{ borderLeftColor: txn.isAdjustment ? "oklch(0.75 0.15 85)" : (txn.type === "cash_in" ? "var(--income)" : "var(--expense)") }}
                >
                  {/* Mobile: stacked layout */}
                  <div className="flex items-start gap-2.5 sm:gap-3">
                    <div
                      className="h-8 w-8 sm:h-10 sm:w-10 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                      style={{
                        backgroundColor: txn.isAdjustment
                          ? "oklch(0.75 0.15 85 / 0.15)"
                          : txn.type === "cash_in"
                            ? "oklch(0.65 0.18 155 / 0.12)"
                            : "oklch(0.58 0.22 25 / 0.12)",
                      }}
                    >
                      {txn.isAdjustment ? (
                        <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: "oklch(0.75 0.15 85)" }} />
                      ) : txn.type === "cash_in" ? (
                        <ArrowDownLeft className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: "var(--income)" }} />
                      ) : (
                        <ArrowUpRight className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: "var(--expense)" }} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      {/* Row 1: merchant + amount */}
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-xs sm:text-sm font-medium text-foreground leading-tight line-clamp-2 sm:truncate">
                          {txn.merchant ?? "Unknown"}
                        </p>
                        <p
                          className="text-xs sm:text-sm font-bold tabular-nums whitespace-nowrap flex-shrink-0"
                          style={{ color: txn.type === "cash_in" ? "var(--income)" : "var(--expense)" }}
                        >
                          {txn.type === "cash_in" ? "+" : "-"}{formatAmount(txn.amount, txn.currency)}
                        </p>
                      </div>
                      {/* Row 2: badges + KWD equivalent */}
                      <div className="flex items-center justify-between gap-1 mt-1">
                        <div className="flex items-center gap-1 flex-wrap min-w-0">
                          {txn.isAdjustment ? (
                            <Badge className="text-[9px] sm:text-[10px] h-4 sm:h-5 px-1 sm:px-1.5 bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border-amber-300 dark:border-amber-700">
                              <AlertTriangle className="h-2 w-2 sm:h-2.5 sm:w-2.5 mr-0.5" />
                              {language === "ar" ? "تسوية" : "Adjustment"}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[9px] sm:text-[10px] h-4 sm:h-5 px-1 sm:px-1.5">
                              {getCategoryLabel(txn.category as string)}
                            </Badge>
                          )}
                          {!txn.isAdjustment && txn.verificationStatus && txn.verificationStatus !== "verified" ? <Badge className={txn.verificationStatus === "disputed" ? "text-[9px] sm:text-[10px] h-4 sm:h-5 px-1 sm:px-1.5 bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300" : "text-[9px] sm:text-[10px] h-4 sm:h-5 px-1 sm:px-1.5 bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"}>{txn.verificationStatus === "disputed" ? t("verification.disputed") : t("verification.provisional")}</Badge> : null}
                          {txn.statementControlStatus === "unavailable" ? <Badge variant="outline" className="text-[9px] sm:text-[10px] h-4 sm:h-5 px-1 sm:px-1.5">{t("sms.pdfControlUnavailable")}</Badge> : null}
                          {txn.statementControlStatus === "forced_mismatch" ? <Badge className="text-[9px] sm:text-[10px] h-4 sm:h-5 px-1 sm:px-1.5 bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">{t("sms.pdfControlForcedMismatch")}</Badge> : null}
                          {txn.bankSource && (
                            <Badge variant="secondary" className="text-[9px] sm:text-[10px] h-4 sm:h-5 px-1 sm:px-1.5 gap-0.5">
                              <Building2 className="h-2 w-2 sm:h-2.5 sm:w-2.5" />
                              {txn.bankSource}
                            </Badge>
                          )}
                        </div>
                        {txn.currency && txn.currency !== "KWD" && txn.amountInKwd && (
                          <div className="text-end text-[10px] sm:text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">
                            <p>≈ {formatAmount(txn.amountInKwd, "KWD")}</p>
                            {txn.fxRateUsed ? <p title={language === "ar" ? `${t("fx.convertedAt")} ${Number(txn.fxRateUsed).toFixed(5)} ${t("fx.on")} ${new Date(txn.transactionDate).toLocaleDateString("ar-KW")}` : `${t("fx.convertedAt")} ${Number(txn.fxRateUsed).toFixed(5)} ${t("fx.on")} ${new Date(txn.transactionDate).toLocaleDateString("en-GB")}`}>{t("fx.convertedAt")} {Number(txn.fxRateUsed).toFixed(5)} {t("fx.on")} {new Date(txn.transactionDate).toLocaleDateString(language === "ar" ? "ar-KW" : "en-GB")}</p> : null}
                          </div>
                        )}
                      </div>
                      {/* Row 3: date + actions */}
                      <div className="flex items-center justify-between mt-1.5">
                        <p className="text-[10px] sm:text-xs text-muted-foreground">
                          {new Date(txn.transactionDate).toLocaleDateString(language === "ar" ? "ar-KW" : undefined, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </p>
                        <div className="flex gap-0.5">
                          <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-8 sm:w-8" onClick={() => openEdit(txn)}>
                            <Pencil className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 sm:h-8 sm:w-8 text-destructive hover:text-destructive"
                            onClick={() => {
                              if (confirm("Delete this transaction?")) {
                                deleteMutation.mutate({ id: txn.id });
                              }
                            }}
                          >
                            <Trash2 className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                          </Button>
                        </div>
                      </div>
                      {txn.verificationStatus === "disputed" ? <p className="mt-1 text-[10px] text-red-700 dark:text-red-300">{t("verification.disputedDescription")}{txn.statementSettlementAmount ? ` ${t("verification.statementSettlement").replace("{amount}", formatAmount(txn.statementSettlementAmount, txn.currency ?? "KWD"))}` : ""}</p> : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editingTxn} onOpenChange={(open) => !open && setEditingTxn(null)}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("transactions.editTitle")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t("field.type")}</Label>
              <Select value={editForm.type} onValueChange={(v) => setEditForm((f) => ({ ...f, type: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash_in">{t("field.cashIn")}</SelectItem>
                  <SelectItem value="cash_out">{t("field.cashOut")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("field.amount")}</Label>
              <Input
                value={editForm.amount}
                onChange={(e) => setEditForm((f) => ({ ...f, amount: e.target.value }))}
                type="text"
              />
            </div>
            <div className="space-y-2">
              <Label>{t("field.merchant")}</Label>
              <Input
                value={editForm.merchant}
                onChange={(e) => setEditForm((f) => ({ ...f, merchant: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("field.category")}</Label>
              <Select value={editForm.category} onValueChange={(v) => setEditForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TRANSACTION_CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {getCategoryLabel(cat)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("field.notes")}</Label>
              <Input
                value={editForm.notes}
                onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Optional notes..."
              />
            </div>
          </div>
          <DialogFooter className="flex-row gap-2">
            <Button variant="outline" onClick={() => setEditingTxn(null)} className="flex-1 sm:flex-none">
              {t("common.cancel")}
            </Button>
            <Button onClick={handleSaveEdit} disabled={updateMutation.isPending} className="flex-1 sm:flex-none">
              {updateMutation.isPending ? t("common.loading") : t("transactions.saveChanges")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
